import React, { useState, useEffect } from 'react';
import { DollarSign, Stethoscope, Users, Calendar, TrendingUp, Activity, Building2, ClipboardList, AlertCircle, RefreshCw, GitBranch, CalendarClock } from 'lucide-react';
import PageLayout from '../PageLayout';
import { useAuth } from '../../context/AuthContext';
import { getAdminStats } from '../../api/dashboard';
import {
  AttendanceTrendChart,
  VisitsTrendChart,
  PendingBreakdownChart,
  EmployeePerformanceList,
  AttendanceTrendPoint,
  VisitsTrendPoint,
  EmployeePerformancePoint,
} from './DashboardCharts';

interface AdminStats {
  total_employees: number;
  total_doctors: number;
  total_firms: number;
  today_attendance: number;
  pending_leaves: number;
  pending_tour_plans: number;
  pending_deviations: number;
  pending_reschedules: number;
  this_month_visits: number;
  attendance_trend: AttendanceTrendPoint[];
  visits_trend: VisitsTrendPoint[];
  employee_performance: EmployeePerformancePoint[];
}

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ElementType;
  color: string;
  bg: string;
  isLoading: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon: Icon, color, bg, isLoading }) => (
  <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
        {isLoading ? (
          <div className="h-8 w-20 bg-gray-200 animate-pulse rounded" />
        ) : (
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        )}
      </div>
      <div className={`p-3 rounded-lg ${bg}`}>
        <Icon className={`w-6 h-6 ${color}`} />
      </div>
    </div>
  </div>
);

interface DashboardProps {
  onLogout?: () => void;
  currentPage?: string;
  onNavigate?: (page: string) => void;
}

const Dashboard: React.FC<DashboardProps> = ({ onLogout, currentPage, onNavigate }) => {
  const { user } = useAuth();
  const tenantId = (user as any)?.tenant_id as number;

  const [stats, setStats] = useState<AdminStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    if (!tenantId) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await getAdminStats(tenantId);
      const data: AdminStats = res.data?.data ?? res.data;
      setStats(data);
    } catch (err) {
      console.error("Error loading dashboard stats:", err);
      setError("Failed to load dashboard data.");
    }
    setIsLoading(false);
  };

  useEffect(() => {
    loadData();
  }, [tenantId]);

  const statCards = [
    {
      title: 'Total Employees',
      value: stats?.total_employees ?? 0,
      icon: Users,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
    },
    {
      title: 'Total Doctors',
      value: stats?.total_doctors ?? 0,
      icon: Stethoscope,
      color: 'text-green-600',
      bg: 'bg-green-50',
    },
    {
      title: 'Total Firms',
      value: stats?.total_firms ?? 0,
      icon: Building2,
      color: 'text-purple-600',
      bg: 'bg-purple-50',
    },
    {
      title: "Today's Attendance",
      value: stats?.today_attendance ?? 0,
      icon: Activity,
      color: 'text-teal-600',
      bg: 'bg-teal-50',
    },
    {
      title: 'This Month Visits',
      value: stats?.this_month_visits ?? 0,
      icon: Calendar,
      color: 'text-orange-600',
      bg: 'bg-orange-50',
    },
    {
      title: 'Pending Leaves',
      value: stats?.pending_leaves ?? 0,
      icon: ClipboardList,
      color: 'text-yellow-600',
      bg: 'bg-yellow-50',
    },
    {
      title: 'Pending Tour Plans',
      value: stats?.pending_tour_plans ?? 0,
      icon: TrendingUp,
      color: 'text-indigo-600',
      bg: 'bg-indigo-50',
    },
    {
      title: 'Pending Deviations',
      value: stats?.pending_deviations ?? 0,
      icon: GitBranch,
      color: 'text-pink-600',
      bg: 'bg-pink-50',
    },
    {
      title: 'Pending Re-schedules',
      value: stats?.pending_reschedules ?? 0,
      icon: CalendarClock,
      color: 'text-red-600',
      bg: 'bg-red-50',
    },
  ];

  return (
    <PageLayout onLogout={onLogout} currentPage={currentPage} onNavigate={onNavigate}>
        <div className="p-4 md:p-8">
          <div className="max-w-7xl mx-auto space-y-8">
            <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
                <p className="text-blue-600 mt-1">Overall management summary and performance metrics.</p>
              </div>
              <button
                onClick={loadData}
                disabled={isLoading}
                className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>

            {error && (
              <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {statCards.map(card => (
                <StatCard key={card.title} {...card} isLoading={isLoading} />
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-stretch">
              <div className="lg:col-span-3">
                <AttendanceTrendChart data={stats?.attendance_trend ?? []} isLoading={isLoading} />
              </div>
              <div className="lg:col-span-2">
                <PendingBreakdownChart
                  pendingLeaves={stats?.pending_leaves ?? 0}
                  pendingTourPlans={stats?.pending_tour_plans ?? 0}
                  pendingDeviations={stats?.pending_deviations ?? 0}
                  pendingReschedules={stats?.pending_reschedules ?? 0}
                  isLoading={isLoading}
                />
              </div>
            </div>

            <VisitsTrendChart data={stats?.visits_trend ?? []} isLoading={isLoading} />

            <EmployeePerformanceList data={stats?.employee_performance ?? []} isLoading={isLoading} />

            <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                  onClick={() => onNavigate?.('employees')}
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left"
                >
                  <Users className="w-6 h-6 text-blue-500 mb-2" />
                  <p className="font-medium text-gray-900">Manage Employees</p>
                  <p className="text-sm text-gray-600">Add, edit, or view employee details</p>
                </button>
                <button
                  onClick={() => onNavigate?.('doctors')}
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left"
                >
                  <Stethoscope className="w-6 h-6 text-green-500 mb-2" />
                  <p className="font-medium text-gray-900">Doctor Database</p>
                  <p className="text-sm text-gray-600">Manage doctor information and contacts</p>
                </button>
                <button
                  onClick={() => onNavigate?.('hr-leave')}
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left"
                >
                  <ClipboardList className="w-6 h-6 text-yellow-500 mb-2" />
                  <p className="font-medium text-gray-900">Leave Requests</p>
                  <p className="text-sm text-gray-600">
                    {isLoading ? 'Loading...' : `${stats?.pending_leaves ?? 0} pending approval`}
                  </p>
                </button>
              </div>
            </div>
          </div>
        </div>
    </PageLayout>
  );
};

export default Dashboard;
